import React, { useState, useEffect } from 'react';
import { Search, Trophy, Medal, Award, User, Calculator, AlertTriangle, CheckCircle, Clock, XCircle, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';

const ResultatsTab = () => {
  const [resultats, setResultats] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);
  
  // États pour les modals
  const [isEligibilityModalOpen, setIsEligibilityModalOpen] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Charger les sessions disponibles
  const fetchSessions = async () => {
    try {
      const response = await api.get('/concours/sessions/');
      if (response.data) {
        const sessionData = response.data.data || response.data.results || [];
        setSessions(Array.isArray(sessionData) ? sessionData : []);
        // Sélectionner la première session par défaut
        if (sessionData.length > 0) {
          setSelectedSession(sessionData[0].id);
          setSelectedSessionData(sessionData[0]);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
      toast.error('Erreur lors du chargement des sessions');
    }
  };

  // Vérifier l'éligibilité pour le calcul des résultats
  const checkEligibility = async (sessionId) => {
    setCheckingEligibility(true);
    try {
      const response = await api.get(`/concours/sessions/${sessionId}/verifier-eligibilite-resultats/`);
      if (response.data && response.data.data) {
        setEligibilityData(response.data.data);
        setIsEligibilityModalOpen(true);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification:', error);
      toast.error('Erreur lors de la vérification de l\'éligibilité');
    } finally {
      setCheckingEligibility(false);
    }
  };

  // Calculer les résultats pour une session
  const calculateResults = async (sessionId) => {
    setCalculating(true);
    try {
      const response = await api.post(`/concours/sessions/${sessionId}/calculer-resultats/`);
      if (response.data && response.data.status === 'success') {
        toast.success(response.data.message);
        // Après calcul, recharger les résultats et les sessions
        fetchResultats();
        fetchSessions();
      }
    } catch (error) {
      console.error('Erreur lors du calcul des résultats:', error);
      
      let errorMsg = 'Erreur lors du calcul des résultats';
      if (error.response && error.response.data) {
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMsg = error.response.data.errors.join('\n');
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        }
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setCalculating(false);
    }
  };

  const fetchResultats = async () => {
    if (!selectedSession) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Utiliser l'endpoint candidats avec filtrage par session
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize,
        session: selectedSession
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/candidats/?${queryParams}`);
      
      if (response.data) {
        const candidatsData = response.data.data || response.data.results || [];
        
        // Filtrer et transformer les candidats en résultats
        const resultatsData = candidatsData
          .filter(candidat => candidat.note_finale !== null && candidat.note_finale !== undefined)
          .map(candidat => ({
            id: candidat.id,
            candidat: candidat.nom_complet || `${candidat.nom} ${candidat.prenom}`,
            numero_candidat: candidat.numero_candidat,
            session: candidat.session_concours?.nom || 'Session inconnue',
            moyenne: candidat.note_finale,
            rang: candidat.rang,
            admis: candidat.admis || false
          }))
          .sort((a, b) => (a.rang || 999) - (b.rang || 999));
        
        setResultats(resultatsData);
        setPagination(response.data.pagination || {
          count: resultatsData.length,
          total_pages: Math.ceil(resultatsData.length / pageSize),
          current_page: currentPage,
          page_size: pageSize
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des résultats');
      setResultats([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Charger les sessions au montage
  useEffect(() => {
    fetchSessions();
  }, []);

  // Charger les résultats quand la session ou les paramètres changent
  useEffect(() => {
    if (selectedSession) {
      const timeoutId = setTimeout(() => {
        fetchResultats();
      }, searchTerm ? 300 : 0);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedSession, currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;
    setSelectedSession(sessionId);
    setCurrentPage(1);
    
    // Trouver les données de la session sélectionnée
    const sessionData = sessions.find(s => s.id.toString() === sessionId);
    setSelectedSessionData(sessionData);
  };

  const handleCheckEligibility = () => {
    if (selectedSession) {
      checkEligibility(selectedSession);
    }
  };

  const handleCalculateResults = () => {
    setIsConfirmModalOpen(true);
  };

  const confirmCalculateResults = () => {
    if (selectedSession) {
      calculateResults(selectedSession);
      setIsConfirmModalOpen(false);
    }
  };

  const getRankIcon = (rang) => {
    if (rang === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (rang === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rang === 3) return <Award className="h-5 w-5 text-orange-500" />;
    return <span className="text-sm font-medium text-gray-600">#{rang}</span>;
  };

  const getGradeColor = (moyenne) => {
    if (moyenne >= 16) return 'text-green-600 bg-green-50';
    if (moyenne >= 12) return 'text-blue-600 bg-blue-50';
    if (moyenne >= 10) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getSessionStatusInfo = (session) => {
    if (!session) return null;
    
    const now = new Date();
    const datePublication = session.date_publication_resultats ? new Date(session.date_publication_resultats) : null;
    
    if (session.resultats_publies) {
      return {
        icon: CheckCircle,
        color: 'text-green-600 bg-green-50',
        message: 'Résultats publiés'
      };
    }
    
    if (session.peut_calculer_resultats) {
      return {
        icon: Calculator,
        color: 'text-blue-600 bg-blue-50',
        message: 'Prêt pour calcul'
      };
    }
    
    if (datePublication && datePublication > now) {
      return {
        icon: Clock,
        color: 'text-yellow-600 bg-yellow-50',
        message: 'En attente de la date de publication'
      };
    }
    
    return {
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-50',
      message: 'Conditions non remplies'
    };
  };

  const renderEligibilityModal = () => {
    if (!eligibilityData) return null;

    return (
      <Modal
        isOpen={isEligibilityModalOpen}
        onClose={() => setIsEligibilityModalOpen(false)}
        title="Vérification d'éligibilité pour le calcul des résultats"
        maxWidth="max-w-4xl"
      >
        <div className="space-y-6">
          {/* Statut global */}
          <div className={`p-4 rounded-lg border ${
            eligibilityData.eligible 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              {eligibilityData.eligible ? (
                <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600 mr-3" />
              )}
              <div>
                <h3 className={`text-lg font-semibold ${
                  eligibilityData.eligible ? 'text-green-800' : 'text-red-800'
                }`}>
                  {eligibilityData.eligible 
                    ? 'Session éligible pour le calcul des résultats' 
                    : 'Session non éligible pour le calcul des résultats'
                  }
                </h3>
                {eligibilityData.resultats_deja_calcules && (
                  <p className="text-sm text-yellow-600 mt-1">
                    ⚠️ Les résultats ont déjà été calculés pour cette session
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Erreurs */}
          {eligibilityData.erreurs && eligibilityData.erreurs.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-red-800 font-semibold mb-3 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                Problèmes détectés ({eligibilityData.erreurs.length})
              </h4>
              <ul className="space-y-2">
                {eligibilityData.erreurs.map((erreur, index) => (
                  <li key={index} className="flex items-start text-red-700">
                    <span className="inline-block w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-sm">{erreur}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Informations détaillées */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-gray-800 font-semibold mb-4 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Informations détaillées
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-blue-600">
                  {eligibilityData.informations.candidats_inscrits}
                </div>
                <div className="text-sm text-gray-600">Candidats inscrits</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-green-600">
                  {eligibilityData.informations.candidats_confirmes}
                </div>
                <div className="text-sm text-gray-600">Candidats confirmés</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-purple-600">
                  {eligibilityData.informations.candidats_presents}
                </div>
                <div className="text-sm text-gray-600">Candidats présents</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-orange-600">
                  {eligibilityData.informations.nombre_epreuves}
                </div>
                <div className="text-sm text-gray-600">Épreuves</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-indigo-600">
                  {eligibilityData.informations.notes_saisies}/{eligibilityData.informations.notes_totales}
                </div>
                <div className="text-sm text-gray-600">Notes saisies</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-teal-600">
                  {eligibilityData.informations.pourcentage_notes}%
                </div>
                <div className="text-sm text-gray-600">Complétude</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setIsEligibilityModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Fermer
            </button>
            {eligibilityData.eligible && (
              <button
                onClick={() => {
                  setIsEligibilityModalOpen(false);
                  handleCalculateResults();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Calculer les résultats
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  if (loading && resultats.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

      return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex gap-4 flex-1">
            {/* Sélecteur de session */}
            <div className="min-w-48">
              <select
                value={selectedSession}
                onChange={handleSessionChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Sélectionner une session</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.nom}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Barre de recherche */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Rechercher un candidat..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Boutons d'action */}
          {selectedSession && (
            <div className="flex gap-2">
              <button
                onClick={handleCheckEligibility}
                disabled={checkingEligibility}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Info className="h-4 w-4 mr-2" />
                {checkingEligibility ? 'Vérification...' : 'Vérifier éligibilité'}
              </button>
              <button
                onClick={handleCalculateResults}
                disabled={calculating || !selectedSessionData?.peut_calculer_resultats}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <Calculator className="h-4 w-4 mr-2" />
                {calculating ? 'Calcul en cours...' : 'Calculer résultats'}
              </button>
            </div>
          )}
        </div>

        {/* Indicateur de statut de la session */}
        {selectedSessionData && (
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedSessionData.nom}</h3>
                  <p className="text-sm text-gray-600">
                    {selectedSessionData.nombre_inscrits} candidat(s) inscrit(s) • 
                    {selectedSessionData.places_restantes} place(s) restante(s)
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {(() => {
                  const statusInfo = getSessionStatusInfo(selectedSessionData);
                  if (!statusInfo) return null;
                  const Icon = statusInfo.icon;
                  return (
                    <div className={`flex items-center px-3 py-2 rounded-full ${statusInfo.color}`}>
                      <Icon className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">{statusInfo.message}</span>
                    </div>
                  );
                })()}
                <div className="text-sm text-gray-500">
                  Statut: <span className="font-medium">{selectedSessionData.statut_display}</span>
                </div>
              </div>
            </div>
          </div>
        )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {!selectedSession ? (
        <EmptyState
          icon={Trophy}
          title="Sélectionner une session"
          description="Veuillez sélectionner une session pour voir les résultats."
        />
      ) : resultats.length === 0 && !loading ? (
        <EmptyState
          icon={Trophy}
          title="Aucun résultat"
          description="Aucun résultat disponible pour cette session. Les notes doivent être saisies et les résultats calculés."
          actionLabel="Calculer résultats"
          onAction={handleCalculateResults}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rang
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° Candidat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Moyenne
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {resultats.map((resultat) => (
                  <tr key={resultat.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center w-10">
                        {getRankIcon(resultat.rang)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{resultat.candidat}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{resultat.numero_candidat || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGradeColor(resultat.moyenne)}`}>
                        {resultat.moyenne}/20
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        resultat.admis ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {resultat.admis ? 'Admis' : 'Non admis'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                pageSize={pageSize}
                totalCount={pagination.count}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal de vérification d'éligibilité */}
      {renderEligibilityModal()}

      {/* Modal de confirmation pour le calcul */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmCalculateResults}
        title="Calculer les résultats"
        message={`Êtes-vous sûr de vouloir calculer les résultats pour la session "${selectedSessionData?.nom}" ?`}
        confirmText="Calculer"
        cancelText="Annuler"
        type="warning"
        isLoading={calculating}
      />
    </div>
  );
};

export default ResultatsTab; 